import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import Navbar from '../components/Navbar';
import CatalogueOTPModal from '../components/CatalogueOTPModal';
import CatalogueViewer from '../components/CatalogueViewer';
import { studioContent } from '../data/studioContent';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';

const StudioDetailPage = () => {
    const { studioName } = useParams();
    const navigate = useNavigate();
    const [selectedStudio, setSelectedStudio] = useState(null);
    const [showOTPModal, setShowOTPModal] = useState(false);
    const [showViewer, setShowViewer] = useState(false);
    const [accessToken, setAccessToken] = useState(null);

    // Studio data - comprehensive content from Word document
    const studios = [
        {
            id: 'globalnest',
            name: 'Global Nest',
            fullName: 'Global Nest - Your Worldwide Furniture Sourcing Hub',
            tagline: 'Connecting the world through furniture',
            icon: 'furniture',
            desc: 'Global Nest is the hub for connecting you to premium furniture solutions across the globe. We specialize in worldwide furniture sourcing and customized OEM/ODM creations, helping brands, retailers, and projects bring their furniture concepts to life with precision, quality, and reliability.',
            image: PLACEHOLDER_IMAGES.nexus,
            category: 'Furniture Sourcing',
            products: ['OEM/ODM Furniture Manufacturing', 'Global Sourcing Solutions', 'Custom Design & Development', 'Quality Assurance & Compliance'],
            designs: 'Worldwide Network',
            color: '#C9A96E',
            keyFeatures: [
                'Worldwide furniture sourcing under one roof',
                'Customized OEM/ODM furniture manufacturing',
                'Strong global supplier and factory network',
                'Quality assurance and international compliance',
                'End-to-end sourcing, development, and logistics support'
            ],
            detailedDesc: 'With a strong global supplier network, Global Nest bridges the gap between design vision and manufacturing excellence. From residential and commercial furniture to bespoke collections, we source, develop, and deliver furniture that meets international standards and tailored specifications.',
            sections: {
                oemOdm: {
                    title: 'Tailored OEM & ODM Furniture Solutions',
                    content: 'At Global Nest, we understand that every brand has a unique identity. Our OEM and ODM services are designed to offer complete flexibility—from material selection and design customization to finishes, packaging, and branding. Whether you need private-label manufacturing or fully customized designs, we ensure seamless execution from concept to production.'
                },
                globalSourcing: {
                    title: 'Global Sourcing, Trusted Partnerships',
                    content: 'Our global sourcing expertise spans multiple countries, ensuring access to skilled craftsmanship, advanced manufacturing, and cost-efficient solutions. We work closely with trusted manufacturers to maintain quality control, ethical practices, and timely delivery—so you can focus on growing your business with confidence.'
                },
                buildingFurniture: {
                    title: 'Building Furniture Without Borders',
                    content: 'Global Nest is more than a sourcing company—we are your global furniture partner. By connecting innovation, craftsmanship, and worldwide resources, we help businesses expand their reach and create furniture that resonates across markets.'
                }
            }
        },
        {
            id: 'artistryroom',
            name: 'ArtistryRoom',
            fullName: 'ArtistryRoom - Discovering Decorative Elegance',
            tagline: 'Where artistry shapes your space',
            icon: 'palette',
            desc: 'ArtistryRoom is where design meets craftsmanship, curating decorative pieces that elevate everyday spaces into expressions of refined taste. Rooted in creativity and inspired by timeless aesthetics, we bring together iconic artifacts and sleek modern forms to create décor that speaks of elegance, balance, and individuality.',
            image: PLACEHOLDER_IMAGES.muse,
            category: 'Decorative Elements',
            products: ['Signature Décor & Artifacts', 'Sleek Metal Forms', 'PVC Finishing Solutions', 'Modern Space Design'],
            designs: 'Curated Collections',
            color: '#B8955A',
            keyFeatures: [
                'Premium signature décor and artistic artifacts',
                'Sleek metal designs with durable PVC finishing',
                'Thoughtfully curated for modern and elegant spaces',
                'Perfect balance of aesthetics and functionality',
                'Crafted to elevate everyday interiors'
            ],
            detailedDesc: 'From statement accents to functional artistry, every ArtistryRoom creation is thoughtfully designed to complement both contemporary and classic interiors.',
            sections: {
                curatedDecor: {
                    title: 'Curated Signature Décor & Artifacts',
                    content: 'Our signature décor and artifacts are inspired by global art forms and cultural influences, reimagined for modern living. Each piece is crafted to stand out—whether as a focal point or a subtle enhancement—adding character and sophistication to homes, offices, and curated spaces.'
                },
                sleekMetal: {
                    title: 'Sleek Metal Forms with Premium PVC Finishing',
                    content: 'ArtistryRoom specializes in sleek metal décor and metal racks with PVC finishing, combining durability with refined design. The PVC coating ensures a smooth finish, enhanced longevity, and a contemporary look, making our products both stylish and practical for everyday use.'
                },
                modernSpaces: {
                    title: 'Designed for Modern Spaces',
                    content: 'Whether you seek minimal elegance or bold decorative statements, ArtistryRoom offers versatile décor solutions that seamlessly blend form and function. Our collections are designed to suit living rooms, bedrooms, offices, studios, and commercial interiors.'
                },
                elevateSpace: {
                    title: 'Elevate Your Space with ArtistryRoom',
                    content: 'At ArtistryRoom, décor is more than decoration—it is an expression of artistry. Discover pieces that transform spaces, inspire creativity, and bring decorative elegance into every room.'
                }
            }
        },
        {
            id: 'thread',
            name: 'THEAD',
            fullName: 'THEAD - Crafting Comfort, Defining Style',
            tagline: 'Where interiors come together beautifully',
            icon: 'thread',
            desc: 'THEAD is a hub for transforming interiors into warm, elegant, and inviting spaces. With a refined selection of sumptuous rugs, elegant blinds, and plush soft furnishings, we bring together comfort, craftsmanship, and contemporary design to enhance every interior.',
            image: PLACEHOLDER_IMAGES.thread,
            category: 'Textiles & Soft Furnishing',
            products: ['Premium Carpets & Rugs', 'Elegant Curtains & Blinds', 'Plush Soft Furnishings', 'Interior Enhancement Solutions'],
            designs: 'Comfort Collections',
            color: '#D4AF37',
            keyFeatures: [
                'Premium carpets and rugs for timeless interiors',
                'Elegant curtains and blinds with refined finishing',
                'Plush soft furnishings for added comfort and style',
                'Designs that balance beauty, durability, and function',
                'Suitable for residential and commercial interiors'
            ],
            detailedDesc: 'Designed for homes, offices, and commercial spaces, THEAD products blend aesthetics with functionality to create environments that feel as good as they look.',
            sections: {
                premiumCarpets: {
                    title: 'Premium Carpets & Rugs',
                    content: 'Our collection of carpets and rugs is crafted to add texture, warmth, and character to your space. From timeless patterns to modern designs, each piece is made with quality materials that offer durability, comfort, and visual appeal.'
                },
                elegantCurtains: {
                    title: 'Elegant Curtains & Blinds',
                    content: 'THEAD\'s curtains and blinds are designed to complement every interior style. Combining refined fabrics with precise finishing, they offer the perfect balance of light control, privacy, and elegance—tailored to suit modern and classic spaces alike.'
                },
                plushFurnishings: {
                    title: 'Plush Soft Furnishings',
                    content: 'Complete your interiors with our range of soft furnishings, thoughtfully designed to add layers of comfort and sophistication. From subtle accents to statement pieces, our soft furnishings enhance ambiance while maintaining a cohesive design aesthetic.'
                },
                transformSpace: {
                    title: 'Transform Your Space with THEAD',
                    content: 'At THEAD, every product is created with the vision of elevating interiors through texture, tone, and thoughtful design. Discover collections that transform everyday spaces into expressions of comfort and elegance.'
                }
            }
        },
        {
            id: 'craftcove',
            name: 'CraftCove',
            fullName: 'CraftCove - Shaping Timeless Wooden Interiors',
            tagline: 'Where craftsmanship shapes interiors',
            icon: 'wood',
            desc: 'CraftCove is a trusted source for exquisite wood, panels, and doors, dedicated to shaping interiors that stand the test of time. Rooted in craftsmanship and quality, we bring together natural materials and refined finishes to create spaces defined by warmth, strength, and elegance.',
            image: PLACEHOLDER_IMAGES.chisel,
            category: 'Wood & Panels',
            products: ['Premium Teak Wood & Solid Wood', 'Wooden Door Frames & Doors', 'Plywoods & Engineered Woods', 'Decorative Laminates & Décor Sheets'],
            designs: 'Timeless Collections',
            color: '#C9A96E',
            keyFeatures: [
                'Premium teak wood and solid wood materials',
                'High-quality wooden door frames and readymade doors',
                'Reliable plywoods and engineered wood solutions',
                'Stylish laminates and décor sheets',
                'Materials crafted for durability, design, and timeless appeal'
            ],
            detailedDesc: 'From structural essentials to decorative surfaces, CraftCove offers complete wooden solutions for residential, commercial, and architectural interiors.',
            sections: {
                premiumWood: {
                    title: 'Premium Teak Wood & Solid Wood Solutions',
                    content: 'Our collection of teak wood and solid wood materials is selected for its durability, natural beauty, and long-lasting performance. Ideal for doors, frames, and custom applications, our wood products form the foundation of timeless interior design.'
                },
                doorsFrames: {
                    title: 'Doors & Door Frames Crafted for Strength',
                    content: 'CraftCove provides high-quality wooden door frames and readymade doors designed for precision fit, structural integrity, and aesthetic appeal. Each product is crafted to complement modern and traditional interiors alike.'
                },
                panelsEngineered: {
                    title: 'Panels, Plywoods & Engineered Woods',
                    content: 'We offer a wide range of plywoods, engineered woods, laminates, and decorative panels that combine innovation with reliability. Our materials are suitable for furniture, wall cladding, cabinetry, and interior detailing, ensuring both performance and style.'
                },
                decorativeLaminates: {
                    title: 'Decorative Laminates & Décor Sheets',
                    content: 'Enhance your interiors with our laminates and décor sheets, available in a variety of textures, finishes, and designs. From minimal elegance to bold statements, our surface solutions add character and refinement to every space.'
                },
                buildConfidence: {
                    title: 'Build with Confidence, Design with CraftCove',
                    content: 'At CraftCove, we believe interiors should reflect lasting quality and thoughtful design. Our products are crafted to support creativity, functionality, and enduring beauty.'
                }
            }
        },
        {
            id: 'lumi',
            name: 'LumiCeiling',
            fullName: 'LumiCeiling - Elevating Ceilings into Design Statements',
            tagline: 'Where ceilings become statements',
            icon: 'ceiling',
            desc: 'LumiCeiling is a hub dedicated to transforming ceilings into expressions of refined design and architectural elegance. We bring together innovation, precision materials, and contemporary aesthetics to redefine how ceilings enhance interior spaces.',
            image: PLACEHOLDER_IMAGES.aether,
            category: 'Ceiling Systems',
            products: ['Sophisticated Soffit & Gypsum Solutions', 'Precision Aluminium Profiles & Frames', 'Gypsum Boards & Powder', 'Modern Interior Ceiling Systems'],
            designs: 'Architectural Solutions',
            color: '#B8935A',
            keyFeatures: [
                'Premium soffit and gypsum ceiling solutions',
                'High-quality gypsum boards and gypsum powder',
                'Precision-engineered aluminium profiles and frames',
                'Designed for durability, aesthetics, and ease of installation',
                'Ideal for residential and commercial interiors'
            ],
            detailedDesc: 'Designed for residential, commercial, and architectural projects, LumiCeiling solutions combine form, function, and durability to create visually striking and lasting ceiling systems.',
            sections: {
                soffitGypsum: {
                    title: 'Sophisticated Soffit & Gypsum Solutions',
                    content: 'Our range of soffit systems, gypsum boards, and gypsum powder provides flexibility for creative ceiling designs. Engineered for smooth finishes, structural reliability, and seamless installation, these solutions support both minimal and intricate ceiling concepts.'
                },
                aluminiumProfiles: {
                    title: 'Precision Aluminium Profiles & Frames',
                    content: 'LumiCeiling offers high-quality aluminium profiles and aluminium frames that ensure strength, clean lines, and modern appeal. Designed to integrate effortlessly with ceiling systems, our aluminium components provide long-term durability while maintaining a sleek, contemporary look.'
                },
                modernInteriors: {
                    title: 'Designed for Modern Interiors',
                    content: 'From subtle ceiling accents to bold architectural statements, LumiCeiling products empower designers and builders to craft ceilings that elevate the entire space. Our materials are suitable for homes, offices, retail spaces, and large commercial interiors.'
                },
                redefiningCeilings: {
                    title: 'Redefining Ceilings with LumiCeiling',
                    content: 'At LumiCeiling, ceilings are more than structural elements—they are defining design features. Discover solutions that bring light, form, and refinement together seamlessly.'
                }
            }
        },
        {
            id: 'shape',
            name: 'ShapeStudio',
            fullName: 'ShapeStudio - Where Vision Takes Solid Form',
            tagline: 'Precision crafted, purpose built',
            icon: 'stone',
            desc: 'ShapeStudio brings ideas to life by giving vision solid form through expert CNC artistry and deep raw material mastery. Blending advanced precision technology with skilled craftsmanship, we create designs that are accurate, expressive, and built to last.',
            image: PLACEHOLDER_IMAGES.carve,
            category: 'CNC & Materials',
            products: ['Precision CNC Art Works', 'Quality MDF & Particle Boards', 'Custom Interior Solutions', 'Architectural Project Components'],
            designs: 'Precision Crafted',
            color: '#D4AF37',
            keyFeatures: [
                'Expert CNC cutting and artistic detailing',
                'High-quality MDF and particle boards',
                'Precision-driven craftsmanship and consistency',
                'Custom solutions for interior and architectural projects',
                'Reliable materials and refined finishes'
            ],
            detailedDesc: 'From concept-driven detailing to large-scale production, ShapeStudio delivers solutions that meet the evolving needs of modern interiors and architectural projects.',
            sections: {
                precisionCNC: {
                    title: 'Precision CNC Art Works',
                    content: 'Our CNC art works are crafted with high-precision machinery and meticulous attention to detail. Whether it\'s intricate patterns, custom panels, or decorative elements, we transform digital designs into flawless physical forms with consistency and accuracy.'
                },
                qualityMDF: {
                    title: 'Quality MDF & Particle Boards',
                    content: 'ShapeStudio offers premium MDF and particle boards selected for strength, smooth finishing, and versatility. These materials provide the perfect foundation for CNC carving, furniture components, wall panels, and customized interior applications.'
                },
                creativeFunctional: {
                    title: 'Designed for Creative & Functional Spaces',
                    content: 'Our products are ideal for residential, commercial, and retail interiors—supporting creative freedom while ensuring structural reliability. ShapeStudio empowers designers, architects, and manufacturers to turn ideas into tangible, functional outcomes.'
                },
                craftingPrecision: {
                    title: 'Crafting Precision. Shaping Possibilities.',
                    content: 'At ShapeStudio, technology and craftsmanship work in harmony to transform imagination into form. Discover solutions that combine creativity, accuracy, and material excellence.'
                }
            }
        },
        {
            id: 'locknkey',
            name: 'LockNKey',
            fullName: 'LockNKey - Precision That Moves with Grace',
            tagline: 'Where precision meets perfection',
            icon: 'hardware',
            desc: 'LockNKey elevates interiors with precision-engineered hardware, designed to ensure flawless movement, secure functionality, and lasting grace. By blending advanced engineering with refined aesthetics, we deliver hardware solutions that enhance both the performance and appearance of modern spaces.',
            image: PLACEHOLDER_IMAGES.torque,
            category: 'Hardware & Fixtures',
            products: ['Door Handles & Knobs', 'Advanced Locks & Security', 'Smooth Motion Hardware', 'Premium Finishing Solutions'],
            designs: 'Precision Hardware',
            color: '#C9A96E',
            keyFeatures: [
                'Precision-crafted door handles and knobs',
                'Reliable automatic and mortise locking systems',
                'Smooth-action tandem boxes and 360-degree hinges',
                'Durable materials with refined finishing',
                'Designed for modern residential and commercial interiors'
            ],
            detailedDesc: 'From everyday living to high-traffic commercial environments, LockNKey hardware is crafted for reliability, smooth operation, and enduring quality.',
            sections: {
                doorHandles: {
                    title: 'Door Handles & Knobs Crafted for Comfort',
                    content: 'Our range of door handles and knobs combines ergonomic design with premium finishes. Engineered for smooth grip and consistent performance, they add a subtle yet impactful touch to doors across residential and commercial interiors.'
                },
                advancedLocks: {
                    title: 'Advanced Locks for Modern Security',
                    content: 'LockNKey offers high-quality automatic locks and mortise locks that provide enhanced security without compromising ease of use. Designed for precision fitting and durability, our locking systems ensure safety, convenience, and peace of mind.'
                },
                smoothMotion: {
                    title: 'Smooth Motion Hardware Solutions',
                    content: 'Experience effortless movement with our tandem boxes and 360-degree hinges, crafted to deliver silent, stable, and seamless operation. These components are ideal for modular furniture, cabinets, and doors, supporting both functionality and refined design.'
                },
                engineeredPerformance: {
                    title: 'Engineered for Performance. Designed for Elegance.',
                    content: 'At LockNKey, every detail matters. Our hardware solutions are built to perform flawlessly while enhancing the grace and longevity of your interiors.'
                }
            }
        },
        {
            id: 'surface',
            name: 'SurfaceStyle',
            fullName: 'SurfaceStyle - The Perfect Base for Brilliance',
            tagline: 'Where great design begins underfoot',
            icon: 'flooring',
            desc: 'SurfaceStyle is dedicated to crafting the perfect foundation for inspired interiors. With an exquisite range of flooring materials, we bring together design, durability, and innovation to create surfaces that elevate every space.',
            image: PLACEHOLDER_IMAGES.strata,
            category: 'Flooring Solutions',
            products: ['Elegant Tiles for Every Space', 'Natural & Engineered Wood Flooring', 'Floor-Based Paints', 'Premium Surface Solutions'],
            designs: 'Foundation Collections',
            color: '#B8935A',
            keyFeatures: [
                'Premium tiles with refined designs',
                'High-quality wood flooring solutions',
                'Durable and elegant floor-based paints',
                'Materials crafted for longevity and performance',
                'Suitable for residential and commercial interiors'
            ],
            detailedDesc: 'From timeless classics to contemporary finishes, SurfaceStyle flooring solutions are designed to enhance the beauty, comfort, and performance of residential and commercial interiors.',
            sections: {
                elegantTiles: {
                    title: 'Elegant Tiles for Every Space',
                    content: 'Our collection of tiles offers a versatile selection of textures, patterns, and finishes. Crafted for strength and visual appeal, SurfaceStyle tiles are ideal for living spaces, kitchens, bathrooms, and high-traffic areas.'
                },
                woodFlooring: {
                    title: 'Natural & Engineered Wood Flooring',
                    content: 'SurfaceStyle provides premium wood flooring solutions that bring warmth, character, and sophistication to interiors. Designed for lasting performance, our wood floors combine natural beauty with modern engineering.'
                },
                floorPaints: {
                    title: 'Floor-Based Paints for Seamless Finishes',
                    content: 'Our floor-based paints are formulated to deliver smooth coverage, durability, and refined aesthetics. Ideal for both residential and commercial floors, they provide a seamless finish while enhancing surface protection and longevity.'
                },
                styleStrength: {
                    title: 'Designed for Style & Strength',
                    content: 'Every SurfaceStyle product is selected to meet high standards of quality, functionality, and design—ensuring floors that not only look exceptional but perform reliably over time.'
                },
                buildBrilliance: {
                    title: 'Build Brilliance from the Ground Up',
                    content: 'At SurfaceStyle, flooring is more than a surface—it\'s the foundation of inspired design. Discover materials that bring strength, elegance, and brilliance to every step.'
                }
            }
        }
    ];

    // Find studio by name
    const studio = studios.find(s => 
        s.name.toLowerCase().replace(/\s+/g, '-') === studioName?.toLowerCase() ||
        s.id === studioName?.toLowerCase()
    );

    useEffect(() => {
        // Scroll to top when component mounts or studio changes
        window.scrollTo(0, 0);
        
        // If studio not found, redirect to studios page
        if (!studio) {
            navigate('/studios');
            return;
        }

        // Animate page elements
        gsap.from('.studio-detail-hero', {
            y: 50,
            duration: 1,
            ease: 'power3.out'
        });

        gsap.from('.studio-detail-content', {
            y: 30,
            duration: 1,
            delay: 0.3,
            ease: 'power3.out'
        });
    }, [studio, navigate]);

    // Icon rendering helper (same as StudiosPage)
    const renderIcon = (iconName, size = 24) => {
        const icons = {
            lock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
            furniture: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11h20"/><path d="M2 16h20"/><path d="M5 11v10"/><path d="M19 11v10"/></svg>,
            palette: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
            thread: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
            wood: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>,
            ceiling: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
            stone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0L2.7 10.3z"/></svg>,
            hardware: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
            flooring: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
            arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        };
        return icons[iconName] || icons.lock;
    };

    const handleCatalogueAccess = () => {
        setSelectedStudio(studio);
        setShowOTPModal(true);
    };

    const handleOTPSuccess = (token, studioId, studioName) => {
        setAccessToken(token);
        setShowOTPModal(false);
        setShowViewer(true);
    };

    const handleCloseViewer = () => {
        setShowViewer(false);
        setAccessToken(null);
        setSelectedStudio(null);
    };

    const handleCloseOTPModal = () => {
        setShowOTPModal(false);
        setSelectedStudio(null);
    };

    if (!studio) {
        return null; // Will redirect in useEffect
    }

    return (
        <div>
            <Helmet>
                <title>{studio.fullName} - Hav'n Interiors</title>
                <meta name="description" content={`${studio.desc} Explore our ${studio.designs} premium designs and materials.`} />
                <meta name="keywords" content={`${studio.name}, ${studio.category}, ${studio.products.join(', ')}, interior design`} />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section className="studio-detail-hero" style={{ 
                height: '100vh',
                background: `linear-gradient(135deg, rgba(44, 24, 16, 1) 0%, rgba(26, 26, 26, 1) 50%, rgba(44, 24, 16, 1) 100%), url(${studio.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                position: 'relative',
                overflow: 'hidden',
                paddingTop: '120px',
                display: 'flex',
                alignItems: 'center'
            }}>
                {/* Animated Background Pattern */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(201, 169, 110, 0.4) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(201, 169, 110, 0.3) 0%, transparent 50%)'
                }} />

                {/* Back Button */}
                <Link 
                    to="/studios"
                    style={{
                        position: 'absolute',
                        top: '140px',
                        left: '5%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: 'var(--text-light)',
                        textDecoration: 'none',
                        fontSize: '16px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        zIndex: 10,
                        background: 'rgba(44, 24, 16, 1)',
                        padding: '12px 24px',
                        borderRadius: '30px',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(201, 169, 110, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'var(--accent-color)';
                        e.target.style.color = 'var(--text-dark)';
                        e.target.style.transform = 'translateX(-5px)';
                        e.target.style.borderColor = 'var(--accent-color)';
                        e.target.style.boxShadow = '0 8px 25px rgba(201, 169, 110, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(44, 24, 16, 1)';
                        e.target.style.color = 'var(--text-light)';
                        e.target.style.transform = 'translateX(0)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                        e.target.style.boxShadow = 'none';
                    }}
                >
                    {renderIcon('arrowLeft', 20)} Back to Studios
                </Link>

                <div className="hero-content" style={{ 
                    position: 'relative', 
                    zIndex: 2, 
                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
                    width: '100%',
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '0 5%'
                }}>
                    {/* Category Badge */}
                    <div style={{ marginBottom: '30px' }}>
                        <span style={{ 
                            fontSize: '16px',
                            color: 'var(--text-dark)',
                            letterSpacing: '2px',
                            textTransform: 'uppercase',
                            fontWeight: '700',
                            background: 'var(--accent-color)',
                            padding: '12px 30px',
                            borderRadius: '30px',
                            border: '2px solid var(--accent-color)',
                            boxShadow: '0 8px 25px rgba(201, 169, 110, 0.4)',
                            display: 'inline-block'
                        }}>
                            {studio.category}
                        </span>
                    </div>
                    
                    <h1 style={{ 
                        fontSize: '96px',
                        fontWeight: '200',
                        marginBottom: '25px',
                        letterSpacing: '-3px',
                        lineHeight: '0.9',
                        color: 'var(--text-light)',
                        marginTop: '0',
                        textShadow: '0 6px 30px rgba(0, 0, 0, 0.9)'
                    }}>
                        {studio.name}
                    </h1>
                    
                    <p style={{
                        fontSize: '28px',
                        color: 'var(--accent-color)',
                        marginBottom: '35px',
                        fontStyle: 'italic',
                        fontWeight: '400',
                        textShadow: '0 2px 15px rgba(0, 0, 0, 0.7)'
                    }}>
                        {studio.tagline}
                    </p>

                    <p style={{
                        fontSize: '20px',
                        color: 'var(--text-light)',
                        lineHeight: '1.8',
                        marginBottom: '50px',
                        textShadow: '0 2px 15px rgba(0, 0, 0, 0.8)'
                    }}>
                        {studio.desc}
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <button
                            onClick={handleCatalogueAccess}
                            style={{
                                padding: '22px 50px',
                                background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                border: '2px solid var(--accent-color)',
                                borderRadius: '50px',
                                color: 'var(--text-dark)',
                                fontSize: '18px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                transition: 'all 0.4s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '15px',
                                boxShadow: '0 10px 30px rgba(201, 169, 110, 0.5)',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-5px) scale(1.05)';
                                e.target.style.boxShadow = '0 20px 50px rgba(201, 169, 110, 0.7)';
                                e.target.style.background = 'var(--text-light)';
                                e.target.style.borderColor = 'var(--text-light)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0) scale(1)';
                                e.target.style.boxShadow = '0 10px 30px rgba(201, 169, 110, 0.5)';
                                e.target.style.background = 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)';
                                e.target.style.borderColor = 'var(--accent-color)';
                            }}
                        >
                            {renderIcon('lock', 24)} Access Catalogue
                        </button>
                    </div>
                </div>
            </section>

            {/* Studio Details Section */}
            <section className="studio-detail-content" style={{ 
                background: 'linear-gradient(135deg, var(--primary-color) 0%, #1a1a1a 50%, var(--primary-color) 100%)',
                padding: '120px 5%',
                position: 'relative'
            }}>
                {/* Background Pattern */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(201, 169, 110, 0.2) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(201, 169, 110, 0.15) 0%, transparent 50%)'
                }} />

                <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
                    {/* Check if studio has extended content sections */}
                    {(studio.id === 'globalnest' || studio.id === 'artistryroom' || studio.id === 'thread' || studio.id === 'craftcove' || studio.id === 'lumi' || studio.id === 'shape' || studio.id === 'locknkey' || studio.id === 'surface') && studio.sections ? (
                        /* Studios with extended content - use 2-column layout */
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: '60px',
                            alignItems: 'start'
                        }}>
                            {/* Left Column - Studio Info */}
                            <div>
                                <h2 style={{ 
                                    fontSize: '56px',
                                    fontWeight: '200',
                                    marginBottom: '40px',
                                    color: 'var(--text-light)',
                                    letterSpacing: '-2px',
                                    textShadow: '0 2px 20px rgba(0, 0, 0, 0.5)'
                                }}>
                                    Studio <span style={{ color: 'var(--accent-color)' }}>Overview</span>
                                </h2>
                                
                                {/* Detailed Description */}
                                <div style={{ marginBottom: '60px' }}>
                                    <p style={{
                                        fontSize: '18px',
                                        color: 'var(--text-light)',
                                        lineHeight: '1.9',
                                        marginBottom: '30px',
                                        background: 'rgba(201, 169, 110, 0.2)',
                                        padding: '30px',
                                        borderRadius: '20px',
                                        border: '1px solid rgba(201, 169, 110, 0.4)',
                                        boxShadow: '0 8px 25px rgba(201, 169, 110, 0.2)'
                                    }}>
                                        {studio.detailedDesc}
                                    </p>
                                </div>

                                {/* Additional Sections for Studios with Extended Content */}
                                <div style={{ marginBottom: '60px' }}>
                                    {/* Render all sections dynamically */}
                                    {Object.entries(studio.sections).map(([sectionKey, section], index) => (
                                        <div key={sectionKey} style={{ marginBottom: '50px' }}>
                                            <h3 style={{ 
                                                fontSize: '24px',
                                                color: 'var(--accent-color)',
                                                marginBottom: '20px',
                                                fontWeight: '600'
                                            }}>
                                                {section.title}
                                            </h3>
                                            <p style={{
                                                fontSize: '16px',
                                                color: 'var(--text-light)',
                                                lineHeight: '1.8',
                                                background: 'rgba(201, 169, 110, 0.1)',
                                                padding: '25px',
                                                borderRadius: '15px',
                                                border: '1px solid rgba(201, 169, 110, 0.3)'
                                            }}>
                                                {section.content}
                                            </p>
                                            {/* Special styling for Global Nest tagline */}
                                            {studio.id === 'globalnest' && sectionKey === 'buildingFurniture' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    Global Nest – Connecting the world through furniture.
                                                </p>
                                            )}
                                            {/* Special styling for ArtistryRoom tagline */}
                                            {studio.id === 'artistryroom' && sectionKey === 'elevateSpace' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    ArtistryRoom – Where artistry shapes your space.
                                                </p>
                                            )}
                                            {/* Special styling for THEAD tagline */}
                                            {studio.id === 'thread' && sectionKey === 'transformSpace' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    THEAD – Where interiors come together beautifully.
                                                </p>
                                            )}
                                            {/* Special styling for CraftCove tagline */}
                                            {studio.id === 'craftcove' && sectionKey === 'buildConfidence' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    CraftCove – Where craftsmanship shapes interiors.
                                                </p>
                                            )}
                                            {/* Special styling for LumiCeiling tagline */}
                                            {studio.id === 'lumi' && sectionKey === 'redefiningCeilings' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    LumiCeiling – Where ceilings become statements.
                                                </p>
                                            )}
                                            {/* Special styling for ShapeStudio tagline */}
                                            {studio.id === 'shape' && sectionKey === 'craftingPrecision' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    ShapeStudio – Precision crafted, purpose built.
                                                </p>
                                            )}
                                            {/* Special styling for LockNKey tagline */}
                                            {studio.id === 'locknkey' && sectionKey === 'engineeredPerformance' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    LockNKey – Where precision meets perfection.
                                                </p>
                                            )}
                                            {/* Special styling for SurfaceStyle tagline */}
                                            {studio.id === 'surface' && sectionKey === 'buildBrilliance' && (
                                                <p style={{
                                                    fontSize: '18px',
                                                    color: 'var(--accent-color)',
                                                    fontStyle: 'italic',
                                                    fontWeight: '600',
                                                    textAlign: 'center',
                                                    marginTop: '20px'
                                                }}>
                                                    SurfaceStyle – Where great design begins underfoot.
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Column - Studio Image & CTA */}
                            <div>
                                <div style={{
                                    background: `linear-gradient(135deg, rgba(201, 169, 110, 0.4) 0%, rgba(44, 24, 16, 0.8) 100%), url(${studio.image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    borderRadius: '30px',
                                    height: '500px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '50px',
                                    border: '3px solid rgba(201, 169, 110, 0.4)',
                                    boxShadow: '0 20px 60px rgba(201, 169, 110, 0.3)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    {/* Overlay Pattern */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'radial-gradient(circle at center, transparent 30%, rgba(201, 169, 110, 0.3) 70%)',
                                        opacity: 1
                                    }} />

                                    <div style={{
                                        textAlign: 'center',
                                        color: 'var(--text-light)',
                                        position: 'relative',
                                        zIndex: 2,
                                        textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)'
                                    }}>
                                        <div style={{ 
                                            marginBottom: '30px',
                                            background: 'var(--accent-color)',
                                            borderRadius: '50%',
                                            width: '120px',
                                            height: '120px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto',
                                            boxShadow: '0 10px 30px rgba(201, 169, 110, 0.5)',
                                            border: '3px solid var(--text-light)'
                                        }}>
                                            <div style={{ color: 'var(--text-dark)' }}>
                                                {renderIcon(studio.icon, 64)}
                                            </div>
                                        </div>
                                        <h3 style={{ 
                                            fontSize: '48px',
                                            fontWeight: '200',
                                            margin: '0',
                                            letterSpacing: '-1px',
                                            textShadow: '0 6px 25px rgba(0, 0, 0, 0.9)',
                                            color: 'var(--text-light)'
                                        }}>
                                            {studio.name}
                                        </h3>
                                        <p style={{
                                            fontSize: '18px',
                                            color: 'var(--accent-color)',
                                            marginTop: '15px',
                                            fontStyle: 'italic',
                                            fontWeight: '500'
                                        }}>
                                            {studio.category}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={handleCatalogueAccess}
                                    style={{
                                        width: '100%',
                                        padding: '25px',
                                        background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                        border: '3px solid var(--accent-color)',
                                        borderRadius: '20px',
                                        color: 'var(--text-dark)',
                                        fontSize: '20px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        transition: 'all 0.4s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '15px',
                                        boxShadow: '0 15px 40px rgba(201, 169, 110, 0.4)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-5px) scale(1.02)';
                                        e.target.style.boxShadow = '0 25px 60px rgba(201, 169, 110, 0.6)';
                                        e.target.style.background = 'var(--text-light)';
                                        e.target.style.borderColor = 'var(--text-light)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0) scale(1)';
                                        e.target.style.boxShadow = '0 15px 40px rgba(201, 169, 110, 0.4)';
                                        e.target.style.background = 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)';
                                        e.target.style.borderColor = 'var(--accent-color)';
                                    }}
                                >
                                    {renderIcon('lock', 28)} Access {studio.name} Catalogue
                                </button>

                                {/* Additional Info Card */}
                                <div style={{
                                    marginTop: '40px',
                                    padding: '30px',
                                    background: 'rgba(201, 169, 110, 0.15)',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(201, 169, 110, 0.4)',
                                    boxShadow: '0 10px 30px rgba(201, 169, 110, 0.2)'
                                }}>
                                    <h4 style={{
                                        fontSize: '18px',
                                        color: 'var(--accent-color)',
                                        marginBottom: '15px',
                                        fontWeight: '600',
                                        textAlign: 'center'
                                    }}>
                                        Premium Access
                                    </h4>
                                    <p style={{
                                        fontSize: '14px',
                                        color: 'var(--text-light)',
                                        textAlign: 'center',
                                        lineHeight: '1.6',
                                        margin: '0'
                                    }}>
                                        Secure OTP verification ensures exclusive access to our premium design collections and professional resources.
                                    </p>
                                </div>

                                {/* Key Products & Services */}
                                <div style={{ marginTop: '40px', marginBottom: '40px' }}>
                                    <h3 style={{ 
                                        fontSize: '20px',
                                        color: 'var(--accent-color)',
                                        marginBottom: '25px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        textAlign: 'center'
                                    }}>
                                        Key Products & Services
                                    </h3>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                        {studio.products.map((product, i) => (
                                            <span key={i} style={{
                                                padding: '10px 20px',
                                                background: 'rgba(201, 169, 110, 0.2)',
                                                borderRadius: '20px',
                                                fontSize: '14px',
                                                color: 'var(--text-light)',
                                                border: '1px solid rgba(201, 169, 110, 0.4)',
                                                fontWeight: '500',
                                                boxShadow: '0 3px 10px rgba(201, 169, 110, 0.2)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'default',
                                                textAlign: 'center',
                                                display: 'block',
                                                marginBottom: '8px'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = 'var(--accent-color)';
                                                e.target.style.color = 'var(--text-dark)';
                                                e.target.style.transform = 'translateY(-2px)';
                                                e.target.style.boxShadow = '0 6px 15px rgba(201, 169, 110, 0.4)';
                                                e.target.style.borderColor = 'var(--accent-color)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = 'rgba(201, 169, 110, 0.2)';
                                                e.target.style.color = 'var(--text-light)';
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 3px 10px rgba(201, 169, 110, 0.2)';
                                                e.target.style.borderColor = 'rgba(201, 169, 110, 0.4)';
                                            }}
                                            >
                                                {product}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Design Collection */}
                                <div style={{ marginBottom: '40px' }}>
                                    <h3 style={{ 
                                        fontSize: '20px',
                                        color: 'var(--accent-color)',
                                        marginBottom: '25px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        textAlign: 'center'
                                    }}>
                                        Design Collection
                                    </h3>
                                    <div style={{
                                        padding: '30px',
                                        background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.2) 0%, rgba(201, 169, 110, 0.12) 100%)',
                                        borderRadius: '20px',
                                        border: '2px solid rgba(201, 169, 110, 0.4)',
                                        boxShadow: '0 10px 25px rgba(201, 169, 110, 0.2)',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ 
                                            fontSize: '32px',
                                            fontWeight: '200',
                                            color: 'var(--accent-color)',
                                            marginBottom: '12px',
                                            textShadow: '0 2px 10px rgba(201, 169, 110, 0.3)'
                                        }}>
                                            {studio.designs}
                                        </div>
                                        <div style={{ 
                                            fontSize: '14px', 
                                            color: 'var(--text-light)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            fontWeight: '500'
                                        }}>
                                            {renderIcon('lock', 16)} OTP Protected Access
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Studios without extended content - use centered single column layout */
                        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
                            <h2 style={{ 
                                fontSize: '56px',
                                fontWeight: '200',
                                marginBottom: '40px',
                                color: 'var(--text-light)',
                                letterSpacing: '-2px',
                                textShadow: '0 2px 20px rgba(0, 0, 0, 0.5)'
                            }}>
                                Studio <span style={{ color: 'var(--accent-color)' }}>Overview</span>
                            </h2>
                            
                            {/* Studio Image */}
                            <div style={{
                                background: `linear-gradient(135deg, rgba(201, 169, 110, 0.4) 0%, rgba(44, 24, 16, 0.8) 100%), url(${studio.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '30px',
                                height: '400px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '50px',
                                border: '3px solid rgba(201, 169, 110, 0.4)',
                                boxShadow: '0 20px 60px rgba(201, 169, 110, 0.3)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'radial-gradient(circle at center, transparent 30%, rgba(201, 169, 110, 0.3) 70%)',
                                    opacity: 1
                                }} />

                                <div style={{
                                    textAlign: 'center',
                                    color: 'var(--text-light)',
                                    position: 'relative',
                                    zIndex: 2,
                                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)'
                                }}>
                                    <div style={{ 
                                        marginBottom: '30px',
                                        background: 'var(--accent-color)',
                                        borderRadius: '50%',
                                        width: '120px',
                                        height: '120px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto',
                                        boxShadow: '0 10px 30px rgba(201, 169, 110, 0.5)',
                                        border: '3px solid var(--text-light)'
                                    }}>
                                        <div style={{ color: 'var(--text-dark)' }}>
                                            {renderIcon(studio.icon, 64)}
                                        </div>
                                    </div>
                                    <h3 style={{ 
                                        fontSize: '48px',
                                        fontWeight: '200',
                                        margin: '0',
                                        letterSpacing: '-1px',
                                        textShadow: '0 6px 25px rgba(0, 0, 0, 0.9)',
                                        color: 'var(--text-light)'
                                    }}>
                                        {studio.name}
                                    </h3>
                                    <p style={{
                                        fontSize: '18px',
                                        color: 'var(--accent-color)',
                                        marginTop: '15px',
                                        fontStyle: 'italic',
                                        fontWeight: '500'
                                    }}>
                                        {studio.category}
                                    </p>
                                </div>
                            </div>

                            {/* Detailed Description */}
                            <div style={{ marginBottom: '60px' }}>
                                <p style={{
                                    fontSize: '18px',
                                    color: 'var(--text-light)',
                                    lineHeight: '1.9',
                                    marginBottom: '30px',
                                    background: 'rgba(201, 169, 110, 0.2)',
                                    padding: '30px',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(201, 169, 110, 0.4)',
                                    boxShadow: '0 8px 25px rgba(201, 169, 110, 0.2)',
                                    textAlign: 'left'
                                }}>
                                    {studio.detailedDesc}
                                </p>
                            </div>

                            {/* Key Products & Services */}
                            <div style={{ marginBottom: '60px' }}>
                                <h3 style={{ 
                                    fontSize: '24px',
                                    color: 'var(--accent-color)',
                                    marginBottom: '30px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px',
                                    textAlign: 'center'
                                }}>
                                    Key Products & Services
                                </h3>
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {studio.products.map((product, i) => (
                                        <span key={i} style={{
                                            padding: '15px 30px',
                                            background: 'rgba(201, 169, 110, 0.2)',
                                            borderRadius: '30px',
                                            fontSize: '16px',
                                            color: 'var(--text-light)',
                                            border: '2px solid rgba(201, 169, 110, 0.4)',
                                            fontWeight: '600',
                                            boxShadow: '0 5px 15px rgba(201, 169, 110, 0.25)',
                                            transition: 'all 0.3s ease',
                                            cursor: 'default'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = 'var(--accent-color)';
                                            e.target.style.color = 'var(--text-dark)';
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 10px 25px rgba(201, 169, 110, 0.4)';
                                            e.target.style.borderColor = 'var(--accent-color)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'rgba(201, 169, 110, 0.2)';
                                            e.target.style.color = 'var(--text-light)';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 5px 15px rgba(201, 169, 110, 0.25)';
                                            e.target.style.borderColor = 'rgba(201, 169, 110, 0.4)';
                                        }}
                                        >
                                            {product}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Design Collection */}
                            <div style={{ marginBottom: '60px' }}>
                                <h3 style={{ 
                                    fontSize: '24px',
                                    color: 'var(--accent-color)',
                                    marginBottom: '30px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px',
                                    textAlign: 'center'
                                }}>
                                    Design Collection
                                </h3>
                                <div style={{
                                    padding: '40px',
                                    background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.2) 0%, rgba(201, 169, 110, 0.12) 100%)',
                                    borderRadius: '25px',
                                    border: '2px solid rgba(201, 169, 110, 0.4)',
                                    boxShadow: '0 15px 40px rgba(201, 169, 110, 0.25)',
                                    textAlign: 'center',
                                    maxWidth: '600px',
                                    margin: '0 auto'
                                }}>
                                    <div style={{ 
                                        fontSize: '42px',
                                        fontWeight: '200',
                                        color: 'var(--accent-color)',
                                        marginBottom: '15px',
                                        textShadow: '0 3px 15px rgba(201, 169, 110, 0.3)'
                                    }}>
                                        {studio.designs}
                                    </div>
                                    <div style={{ 
                                        fontSize: '16px', 
                                        color: 'var(--text-light)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        fontWeight: '500'
                                    }}>
                                        {renderIcon('lock', 20)} OTP Protected Access Required
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Key Features */}
                    <div style={{ marginBottom: '60px' }}>
                        <h3 style={{ 
                            fontSize: '24px',
                            color: 'var(--accent-color)',
                            marginBottom: '35px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            textAlign: 'center'
                        }}>
                            Why Choose {studio.name}
                        </h3>
                        <div style={{ 
                            background: 'rgba(201, 169, 110, 0.2)',
                            borderRadius: '25px',
                            padding: '40px',
                            border: '1px solid rgba(201, 169, 110, 0.4)',
                            boxShadow: '0 10px 30px rgba(201, 169, 110, 0.2)',
                            maxWidth: '800px',
                            margin: '0 auto'
                        }}>
                            {studio.keyFeatures.map((feature, i) => (
                                <div key={i} style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '20px',
                                    padding: '20px 0',
                                    borderBottom: i < studio.keyFeatures.length - 1 ? '1px solid rgba(201, 169, 110, 0.2)' : 'none'
                                }}>
                                    <div style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        background: 'var(--accent-color)',
                                        marginTop: '8px',
                                        flexShrink: 0,
                                        boxShadow: '0 3px 10px rgba(201, 169, 110, 0.4)'
                                    }} />
                                    <span style={{
                                        fontSize: '16px',
                                        color: 'var(--text-light)',
                                        lineHeight: '1.7',
                                        fontWeight: '400'
                                    }}>
                                        {feature}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" className="footer-logo" />
                <p>&copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.</p>
                <p style={{ marginTop: '10px', fontSize: '14px', opacity: 0.6 }}>Innovate. Integrate. Execute.</p>
            </footer>

            {/* Modals */}
            {showOTPModal && selectedStudio && (
                <CatalogueOTPModal
                    studio={selectedStudio.id}
                    studioName={selectedStudio.fullName}
                    onClose={handleCloseOTPModal}
                    onSuccess={handleOTPSuccess}
                />
            )}

            {showViewer && selectedStudio && accessToken && (
                <CatalogueViewer
                    studio={selectedStudio.id}
                    studioName={selectedStudio.fullName}
                    token={accessToken}
                    onClose={handleCloseViewer}
                />
            )}
        </div>
    );
};

 

export default StudioDetailPage;